import GenericServerError from 'components/GenericServerError'
import SubmitButton from 'components/SubmitButton'
import { parseISO } from 'date-fns'
import { format, utcToZonedTime } from 'date-fns-tz'
import { toFriendlyKitId } from 'helpers/string'
import { useEffect, useState } from 'react'
import { FaArrowLeft, FaEnvelopeOpenText } from 'react-icons/fa'
import { RegisterKitVariables } from '../__generated__/RegisterKit'

export type DraftRegisterKitVariables = Omit<RegisterKitVariables['input'], 'photo', 'kitId'> & {
  photoFile?: File | null
}

const Definition = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <p className="mt-1">
    <strong>{title}: </strong>
    {children}
  </p>
)

interface ReviewUploadedImageProps {
  image: File
}

const ReviewUploadedImage = ({ image }: ReviewUploadedImageProps) => {
  const [previewObjectURL, setPreviewObjectURL] = useState(null)

  useEffect(() => {
    if (!image) {
      return
    }

    const previewUrl = URL.createObjectURL(image)
    setPreviewObjectURL(previewUrl)

    return () => {
      URL.revokeObjectURL(previewUrl)
      setPreviewObjectURL(null)
    }
  }, [setPreviewObjectURL, image])

  const previewImgAlt = 'Your uploaded photo'

  if (!image) {
    return null
  }

  if (!previewObjectURL) {
    return null
  }

  return (
    <p className="mt-1">
      <strong>Your uploaded image:</strong>
      <img className="rapid-photo-preview is-block" src={previewObjectURL} alt={previewImgAlt} />
    </p>
  )
}

interface ReviewDetailsProps {
  kitId: string
  onGoBack: () => void
  onComplete: () => Promise<void>
  mutationVariables: DraftRegisterKitVariables
}

const ReviewDetails = ({ kitId, onGoBack, mutationVariables, onComplete }: ReviewDetailsProps) => {
  const [error, setError] = useState<string>()
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async () => {
    try {
      setSubmitting(true)
      await onComplete()
    } catch (err) {
      setError(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <h1 className="title has-text-midnightBlue is-spaced">
        Kit <span className="is-family-monospace has-text-weight-light">{toFriendlyKitId(kitId)}</span> - Review
        Registration Details
      </h1>

      <p className="mt-4 mb-3">
        Carefully check that your details are correct, these will be on your results certificate.
      </p>

      <Definition title="Name">
        {mutationVariables.firstName} {mutationVariables.lastName}
      </Definition>
      <Definition title="Email">
        <span className="has-text-primary">{mutationVariables.email}</span>
      </Definition>
      <Definition title="Date of birth">{format(parseISO(mutationVariables.dob), 'dd/MM/yy')}</Definition>
      <Definition title="Passport / ID number">{mutationVariables.passportNumber || 'Not provided'}</Definition>

      <Definition title="Sample date">
        {mutationVariables.sampleDate
          ? format(utcToZonedTime(parseISO(mutationVariables.sampleDate), 'Europe/London'), 'dd/MM/yy HH:mm')
          : 'Not known'}
        {mutationVariables.sampleDate && ' (London time)'}
      </Definition>

      {mutationVariables.antigenResult && <Definition title="Result">{mutationVariables.antigenResult}</Definition>}

      <ReviewUploadedImage image={mutationVariables.photoFile} />

      <button type="button" className="button is-text is-size-7 mt-3 pl-0" onClick={onGoBack}>
        <div className="is-flex is-align-items-center">
          <FaArrowLeft className="mr-2" /> Go back and amend details
        </div>
      </button>

      <div className="mt-3 px-5 py-3 has-background-info-light">
        <div className="is-flex is-align-items-center">
          <FaEnvelopeOpenText size="3em" />

          <div className="ml-4">
            <p>
              When you're happy with your details, press 'Submit Registration' and we'll send a verification link to
              your email address: <span className="has-text-primary">{mutationVariables.email}</span>.
            </p>
            <p>
              Once you click the link in your email, we'll be able to process your registration and send results once
              available.
            </p>
          </div>
        </div>
      </div>

      {error && <GenericServerError className="mt-3" />}

      <div className="is-flex is-align-items-center mt-5">
        <button type="button" className="button is-text is-size-7 pl-0" onClick={onGoBack}>
          <div className="is-flex is-align-items-center">
            <FaArrowLeft className="mr-2" /> Back
          </div>
        </button>

        <div className="is-flex-grow-1" />

        <SubmitButton onClick={handleSubmit} className="is-midnightBlue" submitting={submitting}>
          Submit Registration
        </SubmitButton>
      </div>
      <p className="is-size-7 has-text-right mt-1">
        By submitting your registration you consent to our processing your data according to our{' '}
        <a target="_blank" href="/privacy-policy/">
          Privacy Policy
        </a>
        .
      </p>
    </>
  )
}

export default ReviewDetails
